import React, { useLayoutEffect, useRef, useState } from "react";
import { useHiddenTabBar } from "../layout/tabBarVisibility";

export interface PinnedActionBarProps {
  /**
   * Everything this particular bar is, beyond being pinned and being glass on
   * a phone: its bleed, its padding, and the surface it wears on a desktop.
   *
   * The surface is passed plainly — `border-t border-line bg-white` — rather
   * than scoped to `md:`, and that is on purpose twice over. It is what the
   * bar looks like on a wide screen, where there is no glass; and it is what
   * it falls back to on a phone whose browser cannot blur, which is the one
   * case a bar with no fill would be the page showing through its own buttons.
   * The glass below out-ranks it on a phone that can blur, because a `max-md`
   * rule inside `@supports` is written after the plain utilities.
   */
  className?: string;
  /**
   * Whether this bar is the bottom edge of the phone, rather than something
   * sitting above the tab bar.
   *
   * Off by default, because most screens that end in one of these already say
   * so themselves — the form is the thing that claims the edge, not the row of
   * buttons at the end of it, so that the claim lasts exactly as long as the
   * form is open. Pass it where the bar is the only thing that knows.
   */
  claimsBottomEdge?: boolean;
  /**
   * Take the bar out of the flow and hold it against the window instead of
   * sticking it to the foot of whatever box it was written in.
   *
   * Off by default, because `sticky` is the better of the two wherever it
   * works: the bar keeps its place in the column, it cannot cover anything the
   * column did not already have room for, and it stops being a bar the moment
   * the screen is short enough not to need one. It works wherever this is the
   * last thing in the column that *is* the page — which is every form in the
   * app, and why they all pass nothing here.
   *
   * It does not work when the bar is written inside a box that starts
   * somewhere down the page — a column of a stacked grid, say. A sticky
   * element is held inside its own parent, so a bar whose parent begins below
   * the fold is simply not on screen until you have scrolled to it: the button
   * somebody is meant to press without hunting is the one thing they have to
   * hunt for. That is the case this flag is for, and it is the only one. Pass
   * it on a phone only — on a wide screen the bar sits at the foot of a column
   * inside a card, and a fixed one would hang it off the window instead.
   *
   * What goes in the flow in its place is a spacer of exactly the bar's own
   * height, measured rather than guessed: the bar is one button tall today and
   * nothing should have to be edited here the first time it is two.
   */
  floats?: boolean;
  children: React.ReactNode;
}

/**
 * The height of a node, kept current as it changes — or 0 while nothing is
 * being measured.
 *
 * `useLayoutEffect` so the spacer is the right height in the same frame the
 * bar is painted in, rather than the page jumping a row's worth once it has
 * settled. A browser with no `ResizeObserver` — and jsdom, under the test
 * runner — gets the first reading and keeps it.
 */
const useMeasuredHeight = (
  measuring: boolean
): [React.RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!measuring || !node) {
      setHeight(0);
      return;
    }

    const read = () => setHeight(node.getBoundingClientRect().height);
    read();

    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measuring]);

  return [ref, height];
};

/**
 * The bar a screen ends in: its actions, held across the foot of the window.
 *
 * ## Why every screen holds it rather than printing it
 *
 * A long screen scrolls, and the one thing there is to press at the end of it
 * is a button somebody has to go looking for after they have finished — on a
 * phone, past a keyboard that has just closed. Held at the foot it is where
 * their thumb already is, and the screen is finished when they say it is
 * rather than when they reach the bottom. The pre-consultation form argues
 * this at length in `StepActions`; this component is that argument applied to
 * every screen in the app that ends in an action.
 *
 * ## The glass, and why it is a phone's only
 *
 * On a phone the bar paints nothing of its own. No fill, no rule, no shadow —
 * what it does instead is blur what is already there, full width, right to the
 * bottom edge. The page does not stop under the bar and it is not covered by
 * it: it goes soft, and the buttons sit on what it became.
 *
 * A white band is the obvious alternative and it is wrong here, which took
 * finding out. Most of this app's screens are white cards on an off-white
 * page, so a white-tinted bar over them is invisible as glass and reads as a
 * slab with the content chopped off underneath it. Take the fill away and
 * there is nothing left to read as a slab — only the blur, which is the effect
 * itself rather than a surface standing in for it.
 *
 * The saturation boost is the one thing kept beside it: blurring averages
 * colour towards grey, and the boost puts back what the averaging took. 40px
 * is enough that type passing under is colour rather than words, which is what
 * keeps it from competing with the buttons over it.
 *
 * `max-md:` because a desktop has no such problem to solve. There the bar sits
 * at the foot of a column inside a card, with a page either side of it, and
 * the ruled white footer every form in the app already wore is right — it is
 * the bottom of a sheet of paper rather than a layer over a screen. Each
 * caller keeps saying what that surface is; see `className`.
 *
 * Both parts are guarded by `supports-[backdrop-filter]`, and so is the
 * absence of a fill, so that a browser that cannot blur falls back to the
 * caller's own opaque bar rather than to a transparent one.
 *
 * ## Stuck to the column, or held against the window
 *
 * `sticky` by default and `fixed` where a column cannot hold it; `floats` is
 * where that choice is argued.
 */
const PinnedActionBar: React.FC<PinnedActionBarProps> = ({
  className = "",
  claimsBottomEdge = false,
  floats = false,
  children,
}) => {
  useHiddenTabBar(claimsBottomEdge);
  const [barRef, barHeight] = useMeasuredHeight(floats);

  const bar = (
    <div
      ref={barRef}
      className={
        (floats
          ? // Over the page it is blurring, and under everything that covers
            // the page outright: a dialog, a sheet, the select overlays. The
            // tab bar it replaces is `z-10` inside the same shell.
            "fixed inset-x-0 bottom-0 z-20 "
          : "sticky bottom-0 z-10 flex-shrink-0 ") +
        "max-md:supports-[backdrop-filter]:border-transparent " +
        "max-md:supports-[backdrop-filter]:bg-transparent " +
        "max-md:supports-[backdrop-filter]:backdrop-blur-2xl " +
        "max-md:supports-[backdrop-filter]:backdrop-saturate-[1.8] " +
        className
      }
    >
      {children}
    </div>
  );

  if (!floats) return bar;

  return (
    <>
      {/* The room the bar is no longer taking, so the end of the page can
          still be scrolled clear of it. `flex-shrink-0` because the column it
          usually stands in is a flex one, and a spacer that can be squeezed is
          a spacer that stops being the bar's height exactly when the page is
          full enough to need it. */}
      <div
        aria-hidden="true"
        className="flex-shrink-0"
        style={{ height: barHeight }}
      />
      {bar}
    </>
  );
};

export default PinnedActionBar;
